import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { isEqual } from 'lodash-es';
import { Table, TableProps, TableAddon, PagingQuery } from 'tea-component';
import useTableAutotipAddon from '@gp/hooks/useTableAutotipAddon';
import useTablePageableOptionsAddon from '@gp/hooks/useTablePageableOptionsAddon';
import { getMetricsTableRecordKey, generateCharsComparator, buildSortableColumns, getSortedRecords } from '../../utils';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../constants/config';

import type { SortBy } from 'tea-component/lib/table/addons';
import type { IBaseProps } from '@home/types/common';
import type { IMetricsBaseMetric } from '@gp/types/metrics';

export interface IBaseMetricsTableCommonProps<T extends IMetricsBaseMetric> {
  loading?: boolean;
  error?: boolean;
  data?: T[] | null;
  sorts?: SortBy<T>[];
  onSortChange?: (sorts: SortBy<T>[]) => void;
  onRetry?: () => void;
  onReset?: () => void;
}

interface IBaseMetricsTableProps<T extends IMetricsBaseMetric>
  extends IBaseProps,
  Omit<TableProps, 'verticalTop' | 'addons' | 'recordKey' | 'records'>,
  IBaseMetricsTableCommonProps<T> {
  noSearchResult?: boolean;
  sortableKeys: (keyof T)[];
  pagination?: PagingQuery;
}

const { sortable } = Table.addons;

export default function BaseMetricsTable<T extends IMetricsBaseMetric>(props: IBaseMetricsTableProps<T>) {
  const {
    className,
    loading = false,
    error = false,
    columns,
    recordKey = getMetricsTableRecordKey,
    data,
    noSearchResult = false,
    sortableKeys,
    sorts: sortsProps,
    onSortChange: onSortChangeProps,
    pagination: paginationProps,
    onRetry,
    onReset,
    ...restProps
  } = props;

  const [records, setRecords] = useState<T[]>([]);

  const [sorts, setSorts] = useState<SortBy<T>[]>([
    { by: 'metric', order: 'asc', sorter: generateCharsComparator('metric') },
  ]);

  const [pagination, setPagination] = useState<PagingQuery>({
    pageIndex: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const pageableAddon = useTablePageableOptionsAddon({
    ...pagination,
    loading,
    recordCount: data?.length,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    onPagingChange: (payload) => {
      const paginationTmp = { ...pagination, ...payload };
      setPagination(paginationTmp);
    },
  });

  const autotipAddon = useTableAutotipAddon({
    isLoading: loading,

    isError: !!error,
    onRetry,

    isFound: !records?.length,
    onClear: onReset,
  });

  const addons: TableAddon<T>[] = [
    pageableAddon,
    sortable({
      columns: buildSortableColumns(sortableKeys),
      value: sorts,
      onChange: (payload: SortBy<T>[]) => {
        const sortsTmp = payload?.slice(0, 1) || [];
        const paginationTmp = { ...pagination, pageIndex: 1 };
        setPagination(paginationTmp);
        setSorts(sortsTmp);
        onSortChangeProps?.(sortsTmp);
      },
    }),
    autotipAddon,
  ];

  useEffect(() => {
    const recordsTmp = !Array.isArray(data) ? [] : data;
    const paginationTmp = { ...pagination, pageIndex: 1 };
    setPagination(paginationTmp);
    setRecords(recordsTmp);
  }, [data]);

  useEffect(() => {
    if (sortsProps && !isEqual(sortsProps, sorts)) {
      setSorts(sortsProps);
    }
  }, [sortsProps]);

  useEffect(() => {
    if (paginationProps && !isEqual(paginationProps, pagination)) {
      setPagination(paginationProps);
    }
  }, [paginationProps]);

  return (
    <Table
      {...restProps}
      className={classnames(
        'dbg-tab-page__table dbg-sortable-table dbg-pageable-table dbg-autotip-table dbg-cell-expand-table dbg-metrics-page-table',
        className,
        {
          'dbg-metrics-page-table--no-search-result': noSearchResult,
        },
      )}
      verticalTop
      addons={addons}
      columns={columns}
      recordKey={recordKey}
      records={getSortedRecords<T>(records, sorts)}
    />
  );
}
